@import '../../style/focus-outline.scss';

:host {
    width: fit-content;

    --wcs-radio-transition-duration: var(--wcs-semantic-motion-duration-feedback-base);

    // region mode Radio
    --wcs-radio-text-color-default: var(--wcs-semantic-color-text-secondary);
    --wcs-radio-text-font-weight-default: var(--wcs-semantic-font-weight-medium);

    --wcs-radio-text-color-selected: var(--wcs-semantic-color-text-primary);
    --wcs-radio-text-font-weight-selected: var(--wcs-semantic-font-weight-medium);

    --wcs-radio-text-color-disabled: var(--wcs-semantic-color-text-disabled);
    --wcs-radio-text-color-hover: var(--wcs-semantic-color-text-primary);

    --wcs-radio-checkmark-size: 1rem; // for now, we let raw value to let adjust the size based on base font size

    --wcs-radio-gap: var(--wcs-semantic-spacing-base);

    --wcs-radio-outline-color-focus: var(--wcs-semantic-color-border-focus-base); // outline color for focus ring

    --wcs-radio-checkmark-border-width: var(--wcs-semantic-border-width-large);
    --wcs-radio-checkmark-border-color-default: var(--wcs-semantic-color-border-control-indicator-default);
    --wcs-radio-checkmark-border-color-selected: var(--wcs-semantic-color-border-control-indicator-selected);
    --wcs-radio-checkmark-border-color-hover: var(--wcs-semantic-color-border-control-indicator-hover);
    --wcs-radio-checkmark-border-color-disabled: var(--wcs-semantic-color-border-control-indicator-disabled);

    --wcs-radio-checkmark-background-color-default: transparent;
    --wcs-radio-checkmark-background-color-selected: var(--wcs-semantic-color-background-control-indicator-selected);
    --wcs-radio-checkmark-background-color-selected-disabled: var(--wcs-semantic-color-background-control-indicator-disabled);
    --wcs-radio-checkmark-background-color-selected-hover: var(--wcs-semantic-color-background-control-indicator-hover);

    --wcs-radio-checkmark-outline-distance-with-checkmark-circle: var(--wcs-semantic-spacing-small);
    --wcs-radio-checkmark-outline-width: var(--wcs-semantic-border-width-large);
    --wcs-radio-checkmark-outline-color: var(--wcs-semantic-color-foreground-control-indicator-selected);
    --wcs-radio-checkmark-border-radius: var(--wcs-semantic-border-radius-full);

    --wcs-radio-border-radius: var(--wcs-semantic-border-radius-base);
    // endregion

    // region mode Option
    --wcs-radio-option-background-color-default: var(--wcs-semantic-color-background-action-secondary-default);
    --wcs-radio-option-background-color-hover: var(--wcs-semantic-color-background-action-secondary-default);
    --wcs-radio-option-background-color-press: var(--wcs-semantic-color-background-action-secondary-default);
    --wcs-radio-option-background-color-disabled: var(--wcs-semantic-color-background-action-secondary-default);

    --wcs-radio-option-background-color-selected-default: var(--wcs-semantic-color-background-action-primary-default);
    --wcs-radio-option-background-color-selected-hover: var(--wcs-semantic-color-background-action-primary-hover);
    --wcs-radio-option-background-color-selected-press: var(--wcs-semantic-color-background-action-primary-press);
    --wcs-radio-option-background-color-selected-disabled: var(--wcs-semantic-color-background-action-primary-disabled);

    --wcs-radio-option-text-color-default: var(--wcs-semantic-color-foreground-action-secondary-default);
    --wcs-radio-option-text-color-hover: var(--wcs-semantic-color-foreground-action-secondary-default);
    --wcs-radio-option-text-color-press: var(--wcs-semantic-color-foreground-action-secondary-press);

    --wcs-radio-option-text-color-selected-default: var(--wcs-semantic-color-foreground-action-primary-default);
    --wcs-radio-option-text-color-selected-hover: var(--wcs-semantic-color-foreground-action-primary-hover);
    --wcs-radio-option-text-color-selected-press: var(--wcs-semantic-color-foreground-action-primary-press);
    --wcs-radio-option-text-color-disabled: var(--wcs-semantic-color-text-disabled);

    --wcs-radio-option-text-font-weight-default: var(--wcs-semantic-font-weight-roman);
    --wcs-radio-option-text-font-weight-selected: var(--wcs-semantic-font-weight-roman);

    --wcs-radio-option-border-radius: var(--wcs-semantic-border-radius-base);

    --wcs-radio-option-border-width: var(--wcs-semantic-border-width-default);

    --wcs-radio-option-border-color-hover: var(--wcs-semantic-color-border-action-secondary-hover);
    --wcs-radio-option-border-color-press: var(--wcs-semantic-color-border-action-secondary-press);

    --wcs-radio-option-padding-top: var(--wcs-semantic-spacing-base);
    --wcs-radio-option-padding-right: var(--wcs-semantic-spacing-large);
    --wcs-radio-option-padding-bottom: var(--wcs-semantic-spacing-base);
    --wcs-radio-option-padding-left: var(--wcs-semantic-spacing-large);
    // endregion

    /*
    * DO NOT REMOVE:
    * The native input is absolutely positioned.
    * This `position: relative` makes `wcs-radio` its containing block.
    * Without it, the input can be laid out relative to another ancestor higher in the DOM,
    * so when the browser focuses the native control it may scroll the window unexpectedly.
    */
    position: relative;
}

:host([mode=radio]),
:host([mode=horizontal]) {
    input {
        position: absolute;
        opacity: 0;
        height: 1px;
        width: 1px;
        overflow: hidden;
        clip-path: inset(50%);
        white-space: nowrap;
    }

    .label {
        margin-bottom: 0;
        color: var(--wcs-radio-text-color-default);
        font-weight: var(--wcs-radio-text-font-weight-default) !important;
        display: inline-block;

        &:before {
            border-radius: var(--wcs-radio-checkmark-border-radius);
            border: var(--wcs-radio-checkmark-border-width) solid var(--wcs-radio-checkmark-border-color-default);
            position: relative;
            width: var(--wcs-radio-checkmark-size);
            height: var(--wcs-radio-checkmark-size);
            pointer-events: none;
            content: "";
            background-color: var(--wcs-radio-checkmark-background-color-default);
        }
    }
}

:host(:focus-visible) {
    outline: none;

    .label {
        transition: none;
        @include focus-outline($outline-color: var(--wcs-radio-outline-color-focus), $border-radius: var(--wcs-radio-border-radius));
    }
}

:host([disabled]) {
    .label {
        color: var(--wcs-radio-text-color-disabled);
        cursor: not-allowed;

        &:before {
            border-color: var(--wcs-radio-checkmark-border-color-disabled);
        }
    }
}

:host(:not([disabled])) {
    .label {
        cursor: pointer;
    }
}

:host([mode=radio].checked),
:host([mode=horizontal].checked) {
    .label {
        color: var(--wcs-radio-text-color-selected);
        font-weight: var(--wcs-radio-text-font-weight-selected) !important;

        &:before {
            border-color: var(--wcs-radio-checkmark-border-color-selected);
            background-color: var(--wcs-radio-checkmark-background-color-selected);
            outline: solid var(--wcs-radio-checkmark-outline-width) var(--wcs-radio-checkmark-outline-color);
            outline-offset: calc(-1 * var(--wcs-radio-checkmark-outline-distance-with-checkmark-circle));
        }
    }
}

:host([mode=radio][disabled].checked),
:host([mode=horizontal][disabled].checked) {
    .label {
        color: var(--wcs-radio-text-color-disabled);

        &:before {
            border-color: var(--wcs-radio-checkmark-border-color-disabled);
            background-color: var(--wcs-radio-checkmark-background-color-selected-disabled);
        }
    }
}

:host([mode=radio]:hover:not([disabled])),
:host([mode=horizontal]:hover:not([disabled])) {
    .label {
        color: var(--wcs-radio-text-color-hover);

        &:before {
            border-color: var(--wcs-radio-checkmark-border-color-hover);
            background-color: var(--wcs-radio-checkmark-background-color-selected-hover);
        }
    }
}

:host([mode=radio]:hover:not([disabled]):not(.checked)),
:host([mode=horizontal]:hover:not([disabled]):not(.checked)) {
    .label:before {
        background-color: var(--wcs-radio-checkmark-background-color-default);
    }
}

:host([mode=radio]) {
    .label {
        border: none;

        &:before {
            transition: background-color var(--wcs-radio-transition-duration) ease-in-out;
            margin-right: var(--wcs-radio-gap);
            display: inline-block;
            top: 3px;
            box-sizing: border-box;
        }

        &:empty::before {
            margin-right: 0;
        }
    }
}

:host([mode=horizontal]) {
    height: fit-content;

    .label {
        &:before {
            transition: background-color var(--wcs-radio-transition-duration) ease-in-out;
            margin: auto auto var(--wcs-radio-gap);
            display: block;
        }

        &:empty::before {
            margin-bottom: 0;
        }
    }
}

:host([mode=option]) {
    font-weight: var(--wcs-radio-option-text-font-weight-default) !important;

    input {
        position: absolute;
        z-index: -1;
        opacity: 0;
        box-sizing: border-box;
        padding: 0;
    }

    .label {
        padding: var(--wcs-radio-option-padding-top) var(--wcs-radio-option-padding-right) var(--wcs-radio-option-padding-bottom) var(--wcs-radio-option-padding-left);
        margin-bottom: 0;
        color: var(--wcs-radio-option-text-color-default);
        white-space: nowrap;
        border-radius: var(--wcs-radio-option-border-radius);
        transition: background-color var(--wcs-radio-transition-duration) ease-in-out;
        background-color: var(--wcs-radio-option-background-color-default);
    }

    input:focus-visible + .label {
        @include focus-outline($outline-color: var(--wcs-radio-outline-color-focus), $border-radius: var(--wcs-radio-border-radius), $outline-offset: 0);
    }
}

:host([mode=option][disabled]) {
    .label {
        color: var(--wcs-radio-option-text-color-disabled);
        background-color: var(--wcs-radio-option-background-color-disabled);
    }
}

:host([mode=option][disabled].checked) {
    .label {
        color: var(--wcs-radio-option-text-color-disabled);
        background-color: var(--wcs-radio-option-background-color-selected-disabled);
    }
}

:host([mode=option].checked) {
    .label {
        font-weight: var(--wcs-radio-option-text-font-weight-selected) !important;
        color: var(--wcs-radio-option-text-color-selected-default);
        background-color: var(--wcs-radio-option-background-color-selected-default);
    }
}

:host([mode=option]:hover.checked:not([disabled])) {
    .label {
        color: var(--wcs-radio-option-text-color-selected-hover);
        background-color: var(--wcs-radio-option-background-color-selected-hover);
    }
}

:host([mode=option]:active.checked:not([disabled])) {
    .label {
        color: var(--wcs-radio-option-text-color-selected-press);
        background-color: var(--wcs-radio-option-background-color-selected-press);
    }
}

:host([mode=option]:hover:not(.checked):not([disabled])) {
    .label {
        color: var(--wcs-radio-option-text-color-hover);
        background-color: var(--wcs-radio-option-background-color-hover);
        outline: var(--wcs-radio-option-border-width) solid var(--wcs-radio-option-border-color-hover);
    }
}

:host([mode=option]:active:not(.checked):not([disabled])) {
    .label {
        color: var(--wcs-radio-option-text-color-press);
        background-color: var(--wcs-radio-option-background-color-press);
        outline: var(--wcs-radio-option-border-width) solid var(--wcs-radio-option-border-color-press);



    }
}
